import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import styled from "styled-components";
import { registerCompleteRequest } from "../../redux/auth/auth.actions";
import { Eye, EyeOff } from "lucide-react";
import { LOGIN } from "../../routes/routes";

// Styled Components (mirroring Login/Signup design)
const SignupProcessContainer = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  margin: 0;
  background: white;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LeftSection = styled.div`
  flex: 0 0 55%;
  background: linear-gradient(135deg, #5b6ee5 0%, #6f4ea8 50%, #4b2f7f 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15),
    inset 0 -2px 12px rgba(0, 0, 0, 0.25);

  @media (max-width: 1024px) {
    flex-basis: 50%;
  }

  @media (max-width: 900px) {
    flex-basis: 45%;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const IllustrationPlaceholder = styled.div`
  width: 80%;
  height: 80%;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.22);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);

  @media (max-width: 1280px) {
    width: 85%;
    height: 80%;
  }
`;

const RightSection = styled.div`
  flex: 0 0 45%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  @media (max-width: 1024px) {
    flex-basis: 50%;
  }

  @media (max-width: 900px) {
    flex-basis: 55%;
    padding: 1.5rem;
  }

  @media (max-width: 768px) {
    flex-basis: auto;
    width: 100%;
    padding: 1.25rem;
  }
`;

const SignupProcessCard = styled.div`
  width: 100%;
  max-width: 400px;
  background: white;
  border-radius: 12px;
  padding: 3rem 2.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;

  @media (max-width: 900px) {
    max-width: 420px;
  }

  @media (max-width: 768px) {
    max-width: 520px;
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    max-width: 100%;
    padding: 1rem;
  }
`;

const SignupProcessTitle = styled.h1`
  color: #1f2937;
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  text-align: center;

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const SignupProcessSubtitle = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  text-align: center;
  margin: 0 0 2rem 0;

  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin-bottom: 1.5rem;
  }
`;

const SignupProcessForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #374151;
  font-weight: 500;
  font-size: 0.875rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.hasError ? "#ef4444" : "#d1d5db"};
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background: linear-gradient(180deg, #ffffff, #f9fafb);
  color: #1f2937;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.04);

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? "#ef4444" : "#8b5cf6"};
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.06);
  }

  &::placeholder {
    color: #9ca3af;
  }

  @media (max-width: 480px) {
    padding: 0.675rem;
    font-size: 0.95rem;
  }
`;

const FieldErrorMessage = styled.span`
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: -0.25rem;
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: linear-gradient(
    180deg,
    rgba(249, 250, 251, 0.95),
    rgba(243, 244, 246, 0.9)
  );
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);

  &:hover {
    color: #374151;
    background: #ffffff;
    transform: translateY(-50%) translateY(-1px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
  }

  &:focus {
    outline: none;
    color: #8b5cf6;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(180deg, #8b5cf6, #7c3aed);
  color: white;
  border: none;
  padding: 0.875rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1rem;
  box-shadow: 0 10px 20px rgba(139, 92, 246, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);
  outline: none;

  &:hover:not(:disabled) {
    background: linear-gradient(180deg, #7c3aed, #6d28d9);
    transform: translateY(-1px);
    box-shadow: 0 14px 26px rgba(109, 40, 217, 0.36);
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.95rem;
  }
`;

const SignupProcessFooter = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
`;

const LoginLink = styled(Link)`
  color: #8b5cf6;
  text-decoration: none;
  font-weight: 600;
  outline: none;

  &:focus {
    outline: none;
    border: none;
  }

  &:active {
    outline: none;
    border: none;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  color: #6b7280;
  font-size: 1rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 1rem;
  margin-bottom: 1rem;
`;

const ErrorLink = styled(Link)`
  color: #8b5cf6;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

function SignupProcess() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, error } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    password_confirm: "",
  });

  const [errors, setErrors] = useState({
    username: "",
    password: "",
    password_confirm: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationError, setVerificationError] = useState(false);

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      // Redirect to signup if no token
      navigate("/signup");
      return;
    }

    // Verify the token by hitting the verification URL
    const verifyToken = async () => {
      setIsVerifying(true);
      setVerificationError(false);
      
      try {
        // Get base URL from environment
        const DEFAULT_BASE_URL = "http://localhost:8000";
        const API_BASE_URL = import.meta.env.VITE_BASE_URL_DEVELOPMENT || DEFAULT_BASE_URL;
        
        // Construct the verification URL from the token
        const verificationUrl = `${API_BASE_URL}/api/v1/auth/register/verify-email/?token=${token}`;
        
        const response = await fetch(verificationUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          setIsVerifying(false);
        } else {
          setVerificationError(true);
          setIsVerifying(false);
        }
      } catch (error) {
        console.error("Error verifying token:", error);
        setVerificationError(true);
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token, navigate]);

  // Handle API errors
  useEffect(() => {
    if (error?.errors) {
      const apiErrors = error.errors;
      setErrors((prev) => ({
        username: apiErrors.username?.[0] || prev.username,
        password: apiErrors.password?.[0] || prev.password,
        password_confirm: apiErrors.password_confirm?.[0] || prev.password_confirm,
      }));
    }
  }, [error]);

  // Show loading state while verifying
  if (isVerifying) {
    return (
      <LoadingContainer>
        <div>Verifying email...</div>
      </LoadingContainer>
    );
  }

  // Show error state if verification failed
  if (verificationError || !token) {
    return (
      <ErrorContainer>
        <ErrorMessage>
          {verificationError 
            ? "Email verification failed. Please try again." 
            : "Invalid verification token."}
        </ErrorMessage>
        <ErrorLink to="/signup">Go back to Sign Up</ErrorLink>
      </ErrorContainer>
    );
  }

  const validateUsername = (username) => {
    if (!username) {
      return "Username is required.";
    }
    if (username.length < 3) {
      return "Username must be at least 3 characters long.";
    }
    if (username.length > 150) {
      return "Username must be 150 characters or fewer.";
    }
    // Username can only contain letters, numbers, and @/./+/-/_ characters
    const usernameRegex = /^[a-zA-Z0-9@.+_-]+$/;
    if (!usernameRegex.test(username)) {
      return "Username can only contain letters, numbers, and @/./+/-/_ characters.";
    }
    return "";
  };

  const validatePassword = (password) => {
    if (!password) {
      return "Password is required.";
    }
    // Add more password validation if needed
    return "";
  };

  const validatePasswordConfirm = (password, passwordConfirm) => {
    if (!passwordConfirm) {
      return "Please confirm your password.";
    }
    if (password !== passwordConfirm) {
      return "Passwords do not match.";
    }
    return "";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!token) {
      return;
    }

    // Validate all fields
    const usernameError = validateUsername(formData.username);
    const passwordError = validatePassword(formData.password);
    const passwordConfirmError = validatePasswordConfirm(
      formData.password,
      formData.password_confirm
    );

    const newErrors = {
      username: usernameError,
      password: passwordError,
      password_confirm: passwordConfirmError,
    };

    setErrors(newErrors);

    // If there are any errors, don't submit
    if (usernameError || passwordError || passwordConfirmError) {
      return;
    }

    // Submit form
    dispatch(
      registerCompleteRequest({
        formValues: {
          verification_token: token,
          username: formData.username,
          password: formData.password,
          password_confirm: formData.password_confirm,
        },
        func: () => {
          // Navigate to dashboard on success
          navigate("/dashboard");
        },
      })
    );
  };

  return (
    <SignupProcessContainer>
      <LeftSection>
        <IllustrationPlaceholder>
          Illustration Placeholder
        </IllustrationPlaceholder>
      </LeftSection>

      <RightSection>
        <SignupProcessCard>
          <SignupProcessTitle>Complete Sign Up</SignupProcessTitle>
          <SignupProcessSubtitle>
            Create your account to get started
          </SignupProcessSubtitle>

          <SignupProcessForm onSubmit={handleSubmit}>
            <InputGroup>
              <Label htmlFor="username">Username</Label>
              <Input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                placeholder="e.g. John123"
                hasError={!!errors.username}
              />
              {errors.username && <FieldErrorMessage>{errors.username}</FieldErrorMessage>}
            </InputGroup>

            <InputGroup>
              <Label htmlFor="password">Password</Label>
              <PasswordInputWrapper>
                <Input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your password"
                  hasError={!!errors.password}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </PasswordToggle>
              </PasswordInputWrapper>
              {errors.password && <FieldErrorMessage>{errors.password}</FieldErrorMessage>}
            </InputGroup>

            <InputGroup>
              <Label htmlFor="password_confirm">Confirm Password</Label>
              <PasswordInputWrapper>
                <Input
                  type={showPasswordConfirm ? "text" : "password"}
                  id="password_confirm"
                  name="password_confirm"
                  value={formData.password_confirm}
                  onChange={handleInputChange}
                  required
                  placeholder="Confirm your password"
                  hasError={!!errors.password_confirm}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  aria-label={
                    showPasswordConfirm ? "Hide password" : "Show password"
                  }
                >
                  {showPasswordConfirm ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </PasswordToggle>
              </PasswordInputWrapper>
              {errors.password_confirm && (
                <FieldErrorMessage>{errors.password_confirm}</FieldErrorMessage>
              )}
            </InputGroup>

            <SubmitButton type="submit" disabled={loading}>
              {loading ? "Creating Account..." : "Create Account"}
            </SubmitButton>
          </SignupProcessForm>

          <SignupProcessFooter>
            Already have an account? <LoginLink to={LOGIN}>Login</LoginLink>
          </SignupProcessFooter>
        </SignupProcessCard>
      </RightSection>
    </SignupProcessContainer>
  );
}

export default SignupProcess;

